<template>
    <themed-modal
        :name="modalName"
        :title="isEditMode ? 'Edit Contact' : 'New Contact'"
        size="lg"
        @closed="handleClosed"
    >
        <themed-form
            :fields="formFields"
            :model-value="localContact"
            :errors="errors"
            layout="grid"
            :columns="6"
            @input="localContact = $event"
        />

        <template #footer>
            <div class="flex justify-end gap-3">
                <themed-button
                    type="button"
                    variant="secondary"
                    @click="hideModal"
                >
                    Cancel
                </themed-button>
                <themed-button
                    type="button"
                    variant="default"
                    :disabled="loading"
                    @click="saveContact"
                >
                    {{ loading ? 'Saving...' : (isEditMode ? 'Save Contact' : 'Create Contact') }}
                </themed-button>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
    name: 'ContactModal',
    
    components: {
        ThemedModal,
        ThemedForm,
        ThemedButton
    },

    props: {
        contact: {
            type: Object,
            default: null
        },
        attachTo: {
            type: String,
            default: null
        },
        attachToId: {
            type: Number,
            default: null
        },
        mode: {
            type: String,
            default: 'create',
            validator: (v) => ['create', 'edit'].includes(v)
        }
    },

    data() {
        return {
            localContact: {},
            errors: {},
            loading: false,
            formFields: [
                // Row 1: Name, Title, Type
                {
                    name: 'name',
                    type: 'input',
                    label: 'Name',
                    required: true,
                    col: 2
                },
                {
                    name: 'title',
                    type: 'input',
                    label: 'Title',
                    col: 2
                },
                {
                    name: 'type',
                    type: 'select',
                    label: 'Type',
                    options: [
                        { value: '', label: 'Select type' },
                        { value: 'Broker', label: 'Broker' },
                        { value: 'Owner', label: 'Owner' },
                        { value: 'Property Manager', label: 'Property Manager' },
                        { value: 'Municipality', label: 'Municipality' },
                        { value: 'Developer', label: 'Developer' },
                        { value: 'Other', label: 'Other' }
                    ],
                    col: 2
                },
                // Row 2: Company Name
                {
                    name: 'company_name',
                    type: 'input',
                    label: 'Company Name',
                    col: 6
                },
                // Row 2: Email, Phone, Ext, Cell
                {
                    name: 'email',
                    type: 'input',
                    inputType: 'email',
                    label: 'Email',
                    col: 2
                },
                {
                    name: 'phone',
                    type: 'input',
                    inputType: 'tel',
                    label: 'Phone',
                    col: 2
                },
                {
                    name: 'extension',
                    type: 'input',
                    label: 'Ext.',
                    col: 1
                },
                {
                    name: 'cell',
                    type: 'input',
                    inputType: 'tel',
                    label: 'Cell',
                    col: 1
                },
                // Row 3: Comments
                {
                    name: 'comments',
                    type: 'textarea',
                    label: 'Comments',
                    rows: 4,
                    col: 6
                }
            ]
        };
    },

    computed: {
        ...mapState(['contacts']),

        isEditMode() {
            return this.mode === 'edit';
        },

        modalName() {
            return this.isEditMode ? 'contact-edit' : 'contact-new';
        }
    },

    watch: {
        contact: {
            immediate: true,
            handler(newContact) {
                if (newContact && this.isEditMode) {
                    this.localContact = { ...newContact };
                } else if (!this.isEditMode) {
                    this.resetForm();
                }
            }
        }
    },

    methods: {
        hideModal() {
            this.$modal.hide(this.modalName);
        },

        handleClosed() {
            this.errors = {};
            this.loading = false;
            if (!this.isEditMode) {
                this.resetForm();
            }
        },

        resetForm() {
            this.localContact = {
                name: '',
                type: '',
                company_name: '',
                title: '',
                email: '',
                phone: '',
                extension: '',
                cell: '',
                comments: '',
                attachTo: this.attachTo,
                attachToId: this.attachToId
            };
        },

        saveContact() {
            // Prevent double submission
            if (this.loading) {
                return;
            }
            
            this.loading = true;
            this.errors = {};

            const savePromise = this.isEditMode
                ? this.$store.dispatch('saveContact', this.localContact)
                : axios.post('/contacts', this.localContact);

            savePromise
                .then((response) => {
                    // For create mode, response is axios response; for edit mode, it's the store dispatch result
                    const contact = this.isEditMode ? this.localContact : response.data.contact;
                    const message = this.isEditMode ? 'Contact Updated' : response.data.message;
                    
                    // Emit appropriate event based on mode
                    this.$emit(this.isEditMode ? 'contact-updated' : 'contact-created', contact);
                    
                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: message
                    });

                    this.hideModal();
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response?.data?.errors) {
                        this.errors = error.response.data.errors;
                    }
                    
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || `Failed to ${this.isEditMode ? 'update' : 'create'} contact`
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>
